import {PaymentMethodTypes, PaymentPlanType} from "../../../enums";
import {FeatureFlags} from "../../helpers";
import {
  AccountExternal,
  AccountFp,
  AccountSwitcherDetails,
  AccountSwitcherItem,
  BalanceSwitcherDetails,
  BundleBalanceBreakdown,
  CreditReportingType,
  SubProduct,
  Vertical,
} from "./account_models";
import {Address, Email, Phone} from "./communication_models";
import {Deceased} from "./deceased_models";
import {UserDashboardInstallment} from "./payment_plan_models";
import {RepresentationCompany, RepresentationRead} from "./representation_models";

export enum BounceRole {
  USER = "user",
  NO_LOGIN_USER = "no_login_user",
}

export interface MilitaryInfo {
  active: boolean;
  source?: "creditor" | "backoffice";
  principal?: string;
}

export type PreferredLanguage = "english" | "spanish";

export type UnavailableHour = 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20;
export type Weekday = "mon" | "tue" | "wed" | "thu" | "fri" | "sat" | "sun";
export type WeeklyUnavailability = Record<Weekday, UnavailableHour[]>;

export interface FrontendUserRead {
  name: string;
  accounts_switcher: AccountSwitcherItem[];
  email?: string;
  phone?: string;
  address?: Address;
  representation_email?: string;
  represented: boolean;
  ach_allowed: boolean;
  ach_blocked_after_failure?: boolean;
  date_of_birth: string;
}

export interface FrontendUserFpRead {
  name: string;
  balance_switcher?: BalanceSwitcherDetails[];
  email?: string;
  phone?: string;
  address?: Address;
  ach_allowed: boolean;
  date_of_birth: string;
}

export interface IUserDetails {
  first_name: string;
  middle_name?: string;
  last_name: string;
  date_of_birth: string;
  ssn: string;
  address?: string;
  state?: string;
}

export type PreLoginContext = {
  account_id: string;
  balance: number;
  current_creditor: string;
  original_creditor: string;
  partner_marketing_name?: string;
  origination_date: string;
  external_account_id: string;
  public_account_id: string;
  partner_logo_url: string;
  partner_symbol_url?: string;
  last_report_type: CreditReportingType;
  next_report_date?: string;
  lowest_monthly_installment_size?: number;
  lowest_hardship_installment_size?: number;
  has_multi_accounts: boolean;
  account_has_active_plan: boolean;
  active_bundle_payment_plan_account_id?: number;
  vertical?: Vertical | null;
  sub_product?: SubProduct | null;
  partner_code?: string | null;
  product?: string | null;
  card_last_4_digits?: string | null;
  bundle_context?: BundlePreLoginContext;
  primary_email?: string;
};

export type BundleAccountInfo = {
  account_id: number;
  product: string;
  original_creditor: string;
  total_amount: number;
};

export type BundlePreLoginContext = {
  total_bundled_amount: number;
  lowest_monthly_installment_size?: number;
  accounts: BundleAccountInfo[];
};

export type UserContextDetails = {
  public_id: string;
  user_name: string;
  feature_flags: FeatureFlags;
  is_eligible_global_login: boolean;
  pre_login_context?: PreLoginContext;
  new_design_eligible: boolean;
  questionnaire_eligible: boolean;
};

export type NoLoginPartnerContextDetails = UserContextDetails & {
  token: string;
};

export enum ContactAffiliation {
  USER = "user",
  AGENT = "agent",
  COMPANY = "company",
}

interface TaggedContact<T> {
  affiliation: ContactAffiliation;
  contact_str: string;
  value?: T;
}

type TaggedPhone = TaggedContact<Phone> & {phone?: Phone};
type TaggedEmail = TaggedContact<Email> & {email?: Email};
type TaggedAddress = TaggedContact<Address> & {address?: Address};

type UserBase = {
  id: string;
  public_id?: string;
  first_name: string;
  middle_name?: string;
  last_name: string;
  phones: Array<Phone>;
  emails: Array<Email>;
  addresses: Array<Address>;
  preferred_language: PreferredLanguage;
  ssn: string;
  date_of_birth: string;
  communication_preferences: {
    phone_call_preferences: boolean;
    sms_preferences: boolean;
    email_preferences: boolean;
    mail_preferences: boolean;
    whatsapp_preferences: boolean;
    ai_calls_preferences: boolean;
    voicemail_preferences: boolean;
  };
  represented: boolean;
  representation_company: RepresentationCompany;
  representation_read?: RepresentationRead;
  deceased_id?: string;
};

interface BoUser extends UserBase {
  accounts_switcher?: AccountSwitcherDetails[];
  tagged_phones: TaggedPhone[];
  tagged_emails: TaggedEmail[];
  tagged_addresses: TaggedAddress[];
  time_zone?: string;
  email?: string;
  ach_allowed: boolean;
  ach_blocked_after_failure?: boolean;
  current_viewer?: string;
  spouse_comm_allowed?: boolean;
  has_full_ssn: boolean;
  deceased_details?: Deceased;
  deceased: boolean;
  user_unavailability: WeeklyUnavailability;
  military_info?: MilitaryInfo;
  representing_fp_and_tp_accounts?: boolean;
}

export interface UserExternalWithFpAccount extends BoUser {
  account: AccountFp;
}

export interface UserExternalWithTpAccount extends BoUser {
  account: AccountExternal;
}

type UserExternal = UserExternalWithFpAccount | UserExternalWithTpAccount;

export interface PayoffPlan {
  abstract_payment_plan_name: string;
  discount: string;
  amount: string;
}

export type UserDashboardBase = {
  account_public_id: string;
  amount_paid: number;
  amount_unpaid: number;
  next_payment_amount?: number;
  next_payment_timestamp?: number;
  original_debt_amount: number;
  installment_list: UserDashboardInstallment[];
  installment_size_cents: number;
  installment_frequency: string;
  payment_plan_type: PaymentPlanType;
  last_payment_timestamp?: number;
  account_number: string;
  autopay_enrolled: boolean;
  temporary_autopay_enrolled: boolean;
  payment_plan_id: string;
  last_4_digits?: string;
  payment_method_type?: PaymentMethodTypes;
  state?: string;
  total_amount?: number;
  payoff_plan: PayoffPlan;
  has_pending_payments: boolean;
  pending_amount: number;
  can_reschedule: boolean;
  reschedule_block_reason?: "six_month_limit" | "last_installment" | "unavailable";
  bundle_balance_breakdown?: BundleBalanceBreakdown;
  can_retry: boolean;
  retry_block_reason?: RetryBlockReason;
  past_due_amount_cents: number;
  cancellation_date?: string;
};

export type RetryBlockReason = "not_late" | "payment_processing" | "account_blocked" | "active_dispute";

export type {TaggedPhone, TaggedEmail, TaggedAddress, UserExternal};

export type UserDashboard = UserDashboardBase & {
  rewards_program_state?: RewardsProgramState;
};

export type RewardsProgramCurrentState = "active" | "unavailable" | "deactivated" | "freeze";

export interface RewardsProgramState {
  account_id: number;
  milestones: RewardsProgramMilestone[];
  history: RewardsProgramHistory[];
  points: number;
  current_max_points: number;
  points_to_unlock_next_reward?: number;
  program_state: RewardsProgramCurrentState;
  is_eligible_to_redeem_freeze_plan?: boolean;
  has_limited_time_boost?: boolean;
  has_limited_time_scheduled_boost?: boolean;
  limited_time_offer_expiration_date?: string;
  days_remaining_to_resolve_payment?: number;
}

export type RewardMilestoneState = "available" | "unavailable" | "locked" | "pending";

export interface RewardsProgramMilestone {
  points: number;
  value: number;
  state: RewardMilestoneState;
}

export interface RewardsProgramHistory {
  type: "earned" | "pending" | "redeemed" | "reduced";
  description: string;
  date: string;
  points: number;
}
